import { GridState, StateUpdatedEvent } from 'ag-grid-community';
import { cloneDeep } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import {
  FundReservesViewPreferences,
  PreferenceKey,
  PreferenceScope,
} from '../../../data-models/preferences-v3.data-model';
import { DefaultIgnoreList, doOnGridStateUpdated } from '../../../util/ag-grid-utils';
import { PreferencesServiceV3 } from '../../../util/preferences-service-v3';

export function getFundReservesViewPrefs() {
  return PreferencesServiceV3.get().getPreference<FundReservesViewPreferences>(
    PreferenceKey.fundReservesViewState,
    PreferenceScope.USER
  );
}

export function setFundReservesViewPrefs(newPrefs: Partial<FundReservesViewPreferences> = {}) {
  PreferencesServiceV3.get().setPreference<FundReservesViewPreferences>(
    PreferenceKey.fundReservesViewState,
    {
      ...(getFundReservesViewPrefs() ?? {}),
      ...newPrefs,
    },
    PreferenceScope.USER
  );
}

export function deleteFundReservesViewPrefs() {
  const prefs = getFundReservesViewPrefs();
  if (!prefs) {
    return;
  }

  PreferencesServiceV3.get().deletePreference({
    scope: PreferenceScope.USER,
    preferenceKey: PreferenceKey.fundReservesViewState,
  });
}

export function useOnReservesGridStateUpdated() {
  const onGridStateUpdated = useMemo(() => {
    return doOnGridStateUpdated((gridState) => {
      setFundReservesViewPrefs({ gridState });
    }, DefaultIgnoreList);
  }, []);

  return useCallback(
    (event: StateUpdatedEvent) => {
      onGridStateUpdated(event);
    },
    [onGridStateUpdated]
  );
}

export function getInitialGridState() {
  const gridState = getFundReservesViewPrefs()?.gridState;
  if (!gridState) {
    return;
  }
  return { ...removeNullsFromSortModel(cloneDeep(gridState)), partialColumnState: true };
}

/* It looks like StateUpdatedEvent.state sometimes contains nulls in sort.sortModel;
This causes an error when grid is initialized with initial state from prefs */
function removeNullsFromSortModel(gridState?: GridState) {
  if (!gridState?.sort?.sortModel) {
    return gridState;
  }
  gridState.sort.sortModel = gridState.sort.sortModel.filter((item) => item != null);
  return gridState;
}
